import * as React from 'react';
import Box from '@mui/material/Box';
import { DataGridPro, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import { ptBR } from '@mui/x-data-grid/locales';
import { ICliente, IProduto } from '../interfaces/IGeradora';
import { getUrlImagem } from '../helpers';
import { useEffect, useState } from 'react';
import { IUserGroup } from '../interfaces/IContext';
import { IControlPageDatagrid } from '../interfaces/IGlobal';

interface IProps {
  itens: IProduto[]
  loading: boolean
  cliente?: ICliente
  sigUfs?: string
  groups: IUserGroup[]
  controlPage: IControlPageDatagrid
  setControlPage: (control: IControlPageDatagrid) => void
  tipoGrid?: 'padrao' | 'estoque' | 'cta'
}

interface IMaterial {
  valkey: string | null,
  keynam: string | null
}

const columnWidths = {
  small: 140,
  medium: 200,
  large: 220,
  extraLarge: 260,
};

export default function TableItens({
  itens,
  loading,
  cliente,
  sigUfs,
  groups,
  controlPage,
  setControlPage,
  tipoGrid = 'padrao'
}: IProps) {
  const columnsPadrao: GridColDef[] = [
    {
      field: 'id',
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'urlFoto',
      headerName: 'FOTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        return (
          <img
            src={params.value}
            alt="Foto"
            style={{ objectFit: 'cover', width: '90%' }}
          />
        );
      }
    },
    {
      field: 'itemCode',
      headerName: 'REF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'frnName',
      headerName: 'DESCRIÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'codeBars',
      headerName: 'COD EAN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'codDun',
      headerName: 'COD DUN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'marca',
      headerName: 'MARCA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'categoria',
      headerName: 'CATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'subCategoria',
      headerName: 'SUBCATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'linha',
      headerName: 'LINHA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'fonteProduto',
      headerName: 'FONTE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'paisOrigem',
      headerName: 'ORIGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'onHold',
      headerName: 'ON HOLD',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'preco',
      headerName: 'PREÇO SEM DESCONTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const preco = params.row.preco;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(preco);

        return precoFormatado
      }
    },
    {
      field: 'descontoDigitado',
      headerName: '% DE DESCONTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || '0'} %`
      }
    },
    {
      field: 'promoSP',
      headerName: 'PROMOÇÃO DENTRO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || 0} %`;
      },
    },
    {
      field: 'precoSPPM',
      headerName: 'PREÇO COM DESCONTO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const precoSPPM = params.row.precoSPPM;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPPM);

        return precoFormatado
      }
    },
    {
      field: 'promoForaSP',
      headerName: 'PROMOÇÃO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || 0} %`;
      },
    },
    {
      field: 'precoSPFM',
      headerName: 'PREÇO COM DESCONTO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
      renderCell: (params) => {
        const precoSPFM = params.row.precoSPFM;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPFM);

        return precoFormatado
      }
    },
    {
      field: 'totFisico',
      headerName: 'ESTOQUE FÍSICO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totCarteira',
      headerName: 'PEDIDOS EM CARTEIRA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totReserva',
      headerName: 'RESERVAS PARA PEDIDO DE VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'totPicking',
      headerName: 'PRÉ-FATURAS EM CARGA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'emTransito',
      headerName: 'EM TRÂNSITO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'totInspecao',
      headerName: 'EM INSPEÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'emQualidade',
      headerName: 'EM QUALIDADE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'revisao',
      headerName: 'EM REVISÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'dispVenda',
      headerName: 'DISP. PARA VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'dispCarga',
      headerName: 'DISP. PARA CARGA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdEmbarcado',
      headerName: 'EMBARCADO TOTAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'dataPrevisao',
      headerName: 'DT CHEGADA EMBARCADO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdProduzindo',
      headerName: 'PRODUZINDO TOTAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'dataProduzindo',
      headerName: 'DT EMBARQUE PRODUZINDO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'sitEstoque',
      headerName: 'SITUAÇÃO DO ESTOQUE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdContainer',
      headerName: 'ENTRADA DE CONTAINER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const qtdContainer = params.row.qtdContainer

        return `${Number(qtdContainer)}`
      }
    },
    {
      field: 'QtdUni',
      headerName: 'QTD MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const qtdMaster = params.row.QtdUni

        return `${Number(qtdMaster)}`
      }
    },
    {
      field: 'qtdCaixaInner',
      headerName: 'QTD INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'tipoEmbalagem',
      headerName: 'TIPO EMBALAGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'ncmCode',
      headerName: 'NCM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'IPI',
      headerName: 'IPI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const ipi = params.row.IPI;

        return `${Number(ipi)?.toFixed(2) || ''} %`
      }
    },
    {
      field: 'MVA.AC',
      headerName: 'MVA - AC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AC',
      headerName: 'ST - AC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AL',
      headerName: 'MVA - AL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AL)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AL',
      headerName: 'ST - AL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AL)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AM',
      headerName: 'MVA - AM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AM)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AM',
      headerName: 'ST - AM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AM)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AP',
      headerName: 'MVA - AP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AP',
      headerName: 'ST - AP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.BA',
      headerName: 'MVA - BA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.BA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.BA',
      headerName: 'ST - BA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.BA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.CE',
      headerName: 'MVA - CE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.CE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.CE',
      headerName: 'ST - CE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.CE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.DF',
      headerName: 'MVA - DF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.DF)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.DF',
      headerName: 'ST - DF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.DF)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.ES',
      headerName: 'MVA - ES',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.ES)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.ES',
      headerName: 'ST - ES',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.ES)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.GO',
      headerName: 'MVA - GO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.GO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.GO',
      headerName: 'ST - GO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.GO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MA',
      headerName: 'MVA - MA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MA',
      headerName: 'ST - MA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MT',
      headerName: 'MVA - MT',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MT)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MT',
      headerName: 'ST - MT',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MT)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MS',
      headerName: 'MVA - MS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MS',
      headerName: 'ST - MS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MG',
      headerName: 'MVA - MG',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MG)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MG',
      headerName: 'ST - MG',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MG)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PA',
      headerName: 'MVA - PA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PA',
      headerName: 'ST - PA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PB',
      headerName: 'MVA - PB',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PB)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PB',
      headerName: 'ST - PB',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PB)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PR',
      headerName: 'MVA - PR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PR',
      headerName: 'ST - PR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PE',
      headerName: 'MVA - PE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PE',
      headerName: 'ST - PE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PI',
      headerName: 'MVA - PI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PI)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PI',
      headerName: 'ST - PI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PI)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RN',
      headerName: 'MVA - RN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RN)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RN',
      headerName: 'ST - RN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RN)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RS',
      headerName: 'MVA - RS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RS',
      headerName: 'ST - RS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RJ',
      headerName: 'MVA - RJ',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RJ)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RJ',
      headerName: 'ST - RJ',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RJ)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RO',
      headerName: 'MVA - RO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RO',
      headerName: 'ST - RO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RR',
      headerName: 'MVA - RR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RR',
      headerName: 'ST - RR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SC',
      headerName: 'MVA - SC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SC',
      headerName: 'ST - SC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SP',
      headerName: 'MVA - SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SP',
      headerName: 'ST - SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SE',
      headerName: 'MVA - SE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SE',
      headerName: 'ST - SE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.TO',
      headerName: 'MVA - TO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.TO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.TO',
      headerName: 'ST - TO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.TO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'CEST',
      headerName: 'CEST',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'comprimento',
      headerName: 'COMPRIMENTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const comprimento = params.row.comprimento

        return `${Number(comprimento)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'largura',
      headerName: 'LARGURA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const largura = params.row.largura

        return `${Number(largura)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'altura',
      headerName: 'ALTURA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const altura = params.row.altura

        return `${Number(altura)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'pesoBruto',
      headerName: 'PESO BRUTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const pesoBruto = params.row.pesoBruto

        return `${Number(pesoBruto)?.toFixed(2) || ''}`
      }
    },
    {
      field: 'cor',
      headerName: 'COR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'material',
      headerName: 'MATERIAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small
    },
    {
      field: 'outlet',
      headerName: 'OUTLET',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'promocao',
      headerName: 'PROMOÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'cj02',
      headerName: 'CJ02 - Falta Container',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj03',
      headerName: 'CJ03 - Avarias Sucata',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj30',
      headerName: 'CJ30 - Avarias Venda',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj33',
      headerName: 'CJ33 - Defeito Embalagem',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'totalBid',
      headerName: 'COTAÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'qtdMve',
      headerName: 'VENDA MÚLTIPLA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: params => Number(params.row.qtdMve)
    },
    {
      field: 'tipo',
      headerName: 'TIPO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_QtdLas',
      headerName: 'LASTRO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_EmpMax',
      headerName: 'EMPILHAMENTO MÁXIMO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_QtdCam',
      headerName: 'CAMADA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'usu_trocod',
      headerName: 'TROCA DE CÓDIGO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'usu_codsub',
      headerName: 'CÓD. SUBSTITUÍDO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
  ];

  const columnsEstoque: GridColDef[] = [
    {
      field: 'urlFoto',
      headerName: 'FOTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        return (
          <img
            src={params.value}
            alt="Foto"
            style={{ objectFit: 'cover', width: '90%' }}
          />
        );
      }
    },
    {
      field: 'itemCode',
      headerName: 'REF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'frnName',
      headerName: 'DESCRIÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'categoria',
      headerName: 'CATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'subCategoria',
      headerName: 'SUBCATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'linha',
      headerName: 'LINHA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'preco',
      headerName: 'PREÇO SEM DESCONTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const preco = params.row.preco;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(preco);
      }
    },
    {
      field: 'promoSP',
      headerName: 'PROMOÇÃO DENTRO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => `${params?.value || 0} %`,
    },
    {
      field: 'precoSPPM',
      headerName: 'PREÇO COM DESCONTO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const precoSPPM = params.row.precoSPPM;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPPM);
      }
    },
    {
      field: 'promoForaSP',
      headerName: 'PROMOÇÃO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => `${params?.value || 0} %`,
    },
    {
      field: 'precoSPFM',
      headerName: 'PREÇO COM DESCONTO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
      renderCell: (params) => {
        const precoSPFM = params.row.precoSPFM;
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPFM);
      }
    },
    {
      field: 'totFisico',
      headerName: 'ESTOQUE FÍSICO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totCarteira',
      headerName: 'PEDIDOS EM CARTEIRA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totReserva',
      headerName: 'RESERVAS PARA PEDIDO DE VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'qtdMve',
      headerName: 'VENDA MÚLTIPLA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: params => Number(params.row.qtdMve)
    },
    {
      field: 'dispCarga',
      headerName: 'DISP. PARA CARGA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'dispVenda',
      headerName: 'DISP. PARA VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdContainer',
      headerName: 'ENTRADA DE CONTAINER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => `${Number(params.row.qtdContainer)}`
    },
    {
      field: 'QtdUni',
      headerName: 'QTD MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => `${Number(params.row.QtdUni)}`
    },
    {
      field: 'qtdCaixaInner',
      headerName: 'QTD INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'tipoEmbalagem',
      headerName: 'TIPO EMBALAGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'ncmCode',
      headerName: 'NCM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'IPI',
      headerName: 'IPI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const ipi = params.row.IPI;

        return `${Number(ipi)?.toFixed(2) || ''} %`
      }
    },
  ];

  const columnsCTA: GridColDef[] = [
    {
      field: 'id',
      headerClassName: 'super-app-theme--header',
    },
    {
      field: 'urlFoto',
      headerName: 'FOTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        return (
          <img
            src={params.value}
            alt="Foto"
            style={{ objectFit: 'cover', width: '90%' }}
          />
        );
      }
    },
    {
      field: 'itemCode',
      headerName: 'REF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'frnName',
      headerName: 'DESCRIÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'codeBars',
      headerName: 'COD EAN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'codDun',
      headerName: 'COD DUN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'marca',
      headerName: 'MARCA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'categoria',
      headerName: 'CATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'subCategoria',
      headerName: 'SUBCATEGORIA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'linha',
      headerName: 'LINHA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'fonteProduto',
      headerName: 'FONTE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'paisOrigem',
      headerName: 'ORIGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'onHold',
      headerName: 'ON HOLD',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'preco',
      headerName: 'PREÇO SEM DESCONTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const preco = params.row.preco;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(preco);

        return precoFormatado
      }
    },
    {
      field: 'descontoDigitado',
      headerName: '% DE DESCONTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || '0'} %`
      }
    },
    {
      field: 'promoSP',
      headerName: 'PROMOÇÃO DENTRO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || 0} %`;
      },
    },
    {
      field: 'precoSPPM',
      headerName: 'PREÇO COM DESCONTO SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const precoSPPM = params.row.precoSPPM;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPPM);

        return precoFormatado
      }
    },
    {
      field: 'promoForaSP',
      headerName: 'PROMOÇÃO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        return `${params?.value || 0} %`;
      },
    },
    {
      field: 'precoSPFM',
      headerName: 'PREÇO COM DESCONTO FORA SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
      renderCell: (params) => {
        const precoSPFM = params.row.precoSPFM;

        let precoFormatado = new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(precoSPFM);

        return precoFormatado
      }
    },
    {
      field: 'totFisico',
      headerName: 'ESTOQUE FÍSICO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totCarteira',
      headerName: 'PEDIDOS EM CARTEIRA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'totReserva',
      headerName: 'RESERVAS PARA PEDIDO DE VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'totPicking',
      headerName: 'PRÉ-FATURAS EM CARGA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'emTransito',
      headerName: 'EM TRÂNSITO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'totInspecao',
      headerName: 'EM INSPEÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'emQualidade',
      headerName: 'EM QUALIDADE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'revisao',
      headerName: 'EM REVISÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'dispVenda',
      headerName: 'DISP. PARA VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'dispCarga',
      headerName: 'DISP. PARA CARGA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdEmbarcado',
      headerName: 'EMBARCADO TOTAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'dataPrevisao',
      headerName: 'DT CHEGADA EMBARCADO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdProduzindo',
      headerName: 'PRODUZINDO TOTAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'dataProduzindo',
      headerName: 'DT EMBARQUE PRODUZINDO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'sitEstoque',
      headerName: 'SITUAÇÃO DO ESTOQUE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'qtdContainer',
      headerName: 'ENTRADA DE CONTAINER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const qtdContainer = params.row.qtdContainer

        return `${Number(qtdContainer)}`
      }
    },
    {
      field: 'QtdUni',
      headerName: 'QTD MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const qtdMaster = params.row.QtdUni

        return `${Number(qtdMaster)}`
      }
    },
    {
      field: 'tipoEmbalagem',
      headerName: 'TIPO EMBALAGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'ncmCode',
      headerName: 'NCM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'IPI',
      headerName: 'IPI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: (params) => {
        const ipi = params.row.IPI;

        return `${Number(ipi)?.toFixed(2) || ''} %`
      }
    },
    {
      field: 'MVA.AC',
      headerName: 'MVA - AC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AC',
      headerName: 'ST - AC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AL',
      headerName: 'MVA - AL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AL)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AL',
      headerName: 'ST - AL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AL)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AM',
      headerName: 'MVA - AM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AM)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AM',
      headerName: 'ST - AM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AM)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.AP',
      headerName: 'MVA - AP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.AP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.AP',
      headerName: 'ST - AP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.AP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.BA',
      headerName: 'MVA - BA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.BA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.BA',
      headerName: 'ST - BA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.BA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.CE',
      headerName: 'MVA - CE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.CE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.CE',
      headerName: 'ST - CE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.CE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.DF',
      headerName: 'MVA - DF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.DF)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.DF',
      headerName: 'ST - DF',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.DF)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.ES',
      headerName: 'MVA - ES',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.ES)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.ES',
      headerName: 'ST - ES',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.ES)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MA',
      headerName: 'MVA - MA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MA',
      headerName: 'ST - MA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MT',
      headerName: 'MVA - MT',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MT)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MT',
      headerName: 'ST - MT',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MT)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MS',
      headerName: 'MVA - MS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MS',
      headerName: 'ST - MS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.MG',
      headerName: 'MVA - MG',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.MG)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.MG',
      headerName: 'ST - MG',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.MG)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PA',
      headerName: 'MVA - PA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PA',
      headerName: 'ST - PA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PA)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PB',
      headerName: 'MVA - PB',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PB)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PB',
      headerName: 'ST - PB',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PB)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PR',
      headerName: 'MVA - PR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PR',
      headerName: 'ST - PR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PE',
      headerName: 'MVA - PE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PE',
      headerName: 'ST - PE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.PI',
      headerName: 'MVA - PI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.PI)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.PI',
      headerName: 'ST - PI',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.PI)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RS',
      headerName: 'MVA - RS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RS',
      headerName: 'ST - RS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RS)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RN',
      headerName: 'MVA - RN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RN)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RN',
      headerName: 'ST - RN',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RN)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RJ',
      headerName: 'MVA - RJ',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RJ)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RJ',
      headerName: 'ST - RJ',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RJ)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RO',
      headerName: 'MVA - RO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RO',
      headerName: 'ST - RO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.RR',
      headerName: 'MVA - RR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.RR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.RR',
      headerName: 'ST - RR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.RR)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SC',
      headerName: 'MVA - SC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SC',
      headerName: 'ST - SC',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SC)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SP',
      headerName: 'MVA - SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SP',
      headerName: 'ST - SP',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SP)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.SE',
      headerName: 'MVA - SE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.SE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.SE',
      headerName: 'ST - SE',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.SE)?.toFixed(2) || ''} %`,
    },
    {
      field: 'MVA.TO',
      headerName: 'MVA - TO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.MVA?.TO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'ST.TO',
      headerName: 'ST - TO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
      renderCell: params => `${Number(params?.row?.ST?.TO)?.toFixed(2) || ''} %`,
    },
    {
      field: 'CEST',
      headerName: 'CEST',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'cor',
      headerName: 'COR',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'material',
      headerName: 'MATERIAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small
    },
    {
      field: 'outlet',
      headerName: 'OUTLET',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'promocao',
      headerName: 'PROMOÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'cj02',
      headerName: 'CJ02 - Falta Container',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj03',
      headerName: 'CJ03 - Avarias Sucata',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj30',
      headerName: 'CJ30 - Avarias Venda',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'cj33',
      headerName: 'CJ33 - Defeito Embalagem',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'totalBid',
      headerName: 'COTAÇÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'tipo',
      headerName: 'TIPO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_QtdLas',
      headerName: 'LASTRO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_EmpMax',
      headerName: 'EMPILHAMENTO MÁXIMO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_QtdCam',
      headerName: 'CAMADA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'usu_trocod',
      headerName: 'TROCA DE CÓDIGO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'usu_codsub',
      headerName: 'CÓD. SUBSTITUÍDO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'CodStr',
      headerName: 'CST',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'OriMer',
      headerName: 'ORIGEM FISCAL',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'USU_ConEmb',
      headerName: 'CONTEÚDO EMBALAGEM',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },

    // 🔹 Comercial
    {
      field: 'qtdMve',
      headerName: 'QUANTIDADE MÚLTIPLA VENDAS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
    },

    // 🔹 Compras
    {
      field: 'comprimento',
      headerName: 'COMPRAS - COMPRIMENTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const comprimento = params.row.comprimento

        return `${Number(comprimento)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'largura',
      headerName: 'COMPRAS - LARGURA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const largura = params.row.largura

        return `${Number(largura)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'altura',
      headerName: 'COMPRAS - ALTURA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const altura = params.row.altura

        return `${Number(altura)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'PesLiq',
      headerName: 'COMPRAS - PESO LÍQUIDO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const pesLiq = params.row.PesLiq
        return `${Number(pesLiq)?.toFixed(2) || ''}`
      }
    },
    {
      field: 'pesoBruto',
      headerName: 'COMPRAS - PESO BRUTO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const pesoBruto = params.row.pesoBruto

        return `${Number(pesoBruto)?.toFixed(2) || ''}`
      }
    },

    // 🔹 Capacidade
    {
      field: 'USU_CapMl',
      headerName: 'CAPACIDADE (ML)',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
      renderCell: (params) => {
        const capMl = params.row.USU_CapMl
        return `${Number(capMl)?.toFixed(2) || ''}`
      }
    },

    // 🔹 Caixa Inner
    {
      field: 'qtdCaixaInner',
      headerName: 'QUANTIDADE - CAIXA INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'USU_ComInner',
      headerName: 'COMPRIMENTO - CAIXA INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const comInner = params.row.USU_ComInner
        return `${Number(comInner)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'USU_LarInner',
      headerName: 'LARGURA - CAIXA INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const larInner = params.row.USU_LarInner
        return `${Number(larInner)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'USU_AltInner',
      headerName: 'ALTURA - CAIXA INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const altInner = params.row.USU_AltInner
        return `${Number(altInner)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'USU_PesInner',
      headerName: 'PESO BRUTO - CAIXA INNER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const pesInner = params.row.USU_PesInner
        return `${Number(pesInner)?.toFixed(1) || ''}`
      }
    },

    // 🔹 Caixa Master
    {
      field: 'QtdUni',
      headerName: 'QUANTIDADE - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
    },
    {
      field: 'usu_comudp',
      headerName: 'COMPRIMENTO - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const comudp = params.row.usu_comudp
        return `${Number(comudp)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'USU_LarUdp',
      headerName: 'LARGURA - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const larUdp = params.row.USU_LarUdp
        return `${Number(larUdp)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'usu_altudp',
      headerName: 'ALTURA - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const altudp = params.row.usu_altudp
        return `${Number(altudp)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'usu_pesliq',
      headerName: 'PESO LÍQUIDO - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const pesliq = params.row.usu_pesliq
        return `${Number(pesliq)?.toFixed(1) || ''}`
      }
    },
    {
      field: 'usu_pesbru',
      headerName: 'PESO BRUTO - CAIXA MASTER',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.large,
      renderCell: (params) => {
        const pesbru = params.row.usu_pesbru
        return `${Number(pesbru)?.toFixed(1) || ''}`
      }
    },

    // 🔹 Uso / Cuidados
    {
      field: 'USU_CuiLim',
      headerName: 'CUIDADOS LIMPEZA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
    },
    {
      field: 'USU_Forno',
      headerName: 'FORNO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'USU_Fogao',
      headerName: 'FOGÃO',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.small,
    },
    {
      field: 'microondas',
      headerName: 'MICRO-ONDAS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },
    {
      field: 'USU_Lava',
      headerName: 'LAVA LOUÇAS',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.medium,
    },

    // 🔹 Texto comercial
    {
      field: 'USU_TxtVen',
      headerName: 'TEXTO VENDA',
      headerClassName: 'super-app-theme--header',
      headerAlign: 'center',
      width: columnWidths.extraLarge,
    },
  ]

  const filterFields = columnsPadrao
    .filter(columnPadrao => columnPadrao?.field?.startsWith('MVA') || columnPadrao?.field?.startsWith('ST'))
    .map(columnPadrao => columnPadrao?.field);

  const fieldsFalse = filterFields.reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = false
    return acc;
  }, {});

  const userHasPermission = !!groups?.find(group =>
    group.displayName === 'Espelho - Diretoria' ||
    group.displayName === 'Espelho - Admin'
  );

  const filterFieldsToHideAcsess = columnsPadrao
    .filter(columnPadrao => columnPadrao?.field?.startsWith('cj') ||
      columnPadrao?.field?.startsWith('totalBid') || columnPadrao?.field?.startsWith('qtdMve')
    )
    .map(columnPadrao => columnPadrao?.field);

  const fieldsToHideAccess = filterFieldsToHideAcsess.reduce<Record<string, boolean>>((acc, field) => {
    acc[field] = userHasPermission;
    return acc;
  }, {});

  const ufDestino = cliente?.sigUfs || sigUfs;
  const getColumnVisibilityModel = (): GridColumnVisibilityModel => ({
    ...fieldsFalse,
    ...fieldsToHideAccess,
    id: false,
    ...(ufDestino ? { [`ST.${ufDestino}`]: true } : {}),
  });

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      ...getColumnVisibilityModel(),
    });

  useEffect(() => {
    setColumnVisibilityModel(getColumnVisibilityModel());
  }, []);

  useEffect(() => {
    setColumnVisibilityModel(getColumnVisibilityModel());
  }, [cliente, sigUfs]);

  const columnsSelecionadas = (tipoGrid === 'padrao' ? columnsPadrao : tipoGrid === 'estoque' ? columnsEstoque : columnsCTA)
    .map((column) => {
      if (!column.field?.startsWith('ST.')) return column;

      const uf = column.field.slice(3);
      return {
        ...column,
        renderCell: (params: any) => new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(Number(params.row?.ST?.[uf]) || 0)
      };
    });

  return (
    <Box sx={{
      height: 520,
      width: '100%',
      marginBottom: '8px',
      '& .super-app-theme--header': {
        backgroundColor: '#1a3661',
        color: '#fff'
      },
      // '& .MuiDataGrid-columnSeparator > .MuiSvgIcon-root': {
      //   display: 'none'
      // },
      '& .MuiDaraGrid-header-filter': {
        color: 'blue !important'
      },
      '& .MuiDataGrid-columnHeaderDraggableContainer > .MuiDataGrid-columnHeaderTitleContainer > .MuiDataGrid-iconButtonContainer > .MuiButtonBase-root': {
        color: '#fff'
      },
      '& .MuiDataGrid-columnHeaderDraggableContainer > .MuiDataGrid-menuIcon > .MuiButtonBase-root': {
        color: '#fff'
      },
      '& .MuiDataGrid-root > .MuiDataGrid-main > .MuiDataGrid-virtualScroller > .MuiDataGrid-virtualScrollerContent > .MuiDataGrid-virtualScrollerRenderZone > .MuiDataGrid-row > .MuiDataGrid-cell': {
        border: '1px solid black',
        textAlign: 'center',
      }
    }}>
      <DataGridPro
        rows={itens}
        columns={columnsSelecionadas}
        density={'standard'}
        loading={loading}
        rowHeight={120}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        disableRowSelectionOnClick
        disableMultipleRowSelection
        disableColumnPinning
        disableColumnReorder
        columnVisibilityModel={columnVisibilityModel}
        paginationModel={{
          pageSize: controlPage.limit,
          page: controlPage.page
        }}
        onPaginationModelChange={(model) => {
          setControlPage({
            page: model.page,
            limit: model.pageSize
          });
        }}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false
            }
          },
          pagination: {
            paginationModel: {
              pageSize: controlPage.limit
            }
          }
        }}
        pagination
      />
    </Box>
  )
}
